import * as _ from 'lodash';

const TOO_MANY_REQUESTS_CODE = 429;

// An APIError holds an error message. This is just a string but can be interfaced as with any Error.
// This design is meant to be compatible with the many places throughout the codebase where an IAsyncStatus object
// holds strings or Error or APIError objects.
export class APIError extends String implements Error {
  get message(): string {
    return this.toString();
  }

  get name(): string {
    return 'APIError';
  }
}

export type FailedFetchResponse =
  // A Response represents a response with a non-200 status.
  // The data property may be set by fetch-dedupe.
  | Response & { data?: unknown }
  // If a request fails because the fetch function threw an error (e.g., there's no internet connection), we don't have
  // a Response at all.
  | Error
  // If we don't have a Response or Error object, then the request was aborted by the browser. This can confuse the
  // promise middleware we use for Redux, in which case we can have an action dispatched with an empty payload.
  | {}
  | null;

// errorMessageFromResponse returns an error message extracted from the response or error object or falls back to a
// generic error message if a concrete error message can't be extracted.
export const errorMessageFromResponse = (resp: FailedFetchResponse): APIError & string => {
  let message: string;

  // resp is compared with {} to determine if it's empty rather than using the isEmpty function because lodash considers
  // Error instances to be empty.
  if (!resp || _.isEqual(resp, {}) || (resp.status === 0 && !resp.body && !_.isError(resp))) {
    message = 'Could not make the request.';
  } else if (resp.data) {
    if (resp.data.error) {
      message = resp.data.error.message || resp.data.error;
    } else if (resp.data.errors?.length && Array.isArray(resp.data.errors)) {
      message = resp.data.errors.map(e => e?.message || e || 'Unknown error').join('\n\n');
    } else if (resp.data.errors?.length) {
      message = resp.data.errors;
    } else if (resp.data.message) {
      message = resp.data.message;
    } else if (_.isString(resp.data)) {
      message = resp.data;
    }
  } else if (resp.status === TOO_MANY_REQUESTS_CODE) {
    //  429 (Too many requests) responses do not have data or message body.
    message = 'Too many requests';
  } else if (_.isNumber(resp.status) && !_.isError(resp)) {
    // We have a Response (with a non-zero status), but the body wasn't parsed.
    message = `An error occurred (status ${resp.status}).`;
  } else if (resp.message) {
    message = resp.message;
  }

  if (!message) {
    message = 'An error occurred.';
  }

  // An APIError is effectively a string, but TypeScript can't prove it. Someday we'll drop the APIError class and just
  // return a string, but we need to update the places in the code that rely on a "message" property of the Error type.
  return new APIError(message) as APIError & string;
};

// errorMessagesListFromResponse returns error message extracted from resp, wrapped in an array for the places where
// we store multiple errors in an IAsyncStatus object.
export const errorMessagesListFromResponse = (resp: FailedFetchResponse) => [errorMessageFromResponse(resp)];
